import { createApi } from "@reduxjs/toolkit/query/react";
import baseQueryWithReauth from "../../utils/basequery";

export const campaignApi = createApi({
  reducerPath: "campaignApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["campaigns"],
  endpoints: (builder) => ({
    createCampaign: builder.mutation({
      query: (body) => ({
        url: "campaign/batch",
        method: "POST",
        body,
      }),
      invalidatesTags: ["campaigns"],
    }),
    getCampaigns: builder.query({
      query: (args) => {
        const { pagination, sorting, searchQuery } = args;
        return {
          url: "campaign",
          params: {
            page: pagination.pageIndex,
            rows: pagination.pageSize,
            sorting: JSON.stringify(sorting),
            search: searchQuery,
          },
        };
      },
      providesTags: ["campaigns"],
    }),
    getSingleCampaign: builder.query({
      query: (id) => ({
        url: `campaign/${id}`,
      }),
      providesTags: (result, error, id) => [
        { type: "campaigns", id },
        "campaigns",
      ],
    }),
    getGroupedCampaigns: builder.query({
      query: (campaignIds) => {
        const ids = Array.isArray(campaignIds)
          ? campaignIds.join(",")
          : campaignIds;
        return {
          url: `campaign/grouped`,
          params: {
            ids: ids,
          },
        };
      },
    }),
    getCampaignUsers: builder.query({
      query: (id) => ({
        url: `campaign/users/${id}`,
      }),
    }),
    getApolloMessage: builder.query({
      query: (args) => {
        const { page, rows } = args;
        return {
          url: "apolo-messages/sequences",
          params: { page, rows },
          method: "GET",
        };
      },
      transformResponse: (response) => {
        return response;
      },
    }),
    getApolloMessageDetail: builder.query({
      query: (args) => {
        const { id, page, rows } = args;
        return {
          url: `apolo-messages/sequence/${id}`,
          params: { page, rows },
        };
      },
    }),
    getSendgridMessages: builder.query({
      query: () => {
        return {
          url: `sendgrid/messages`,
        };
      },
    }),
    getDraftCampaigns: builder.query({
      query: () => {
        return {
          url: `campaign/draft`,
          method: "GET",
        };
      },
      providesTags: ["campaigns"],
    }),
    getCampaignBatchNumbers: builder.query({
      query: () => {
        return {
          url: `campaign/batches`,
          method: "GET",
        };
      },
      providesTags: ["campaigns"],
    }),
    getCampaignVersionHistory: builder.query({
      query: (campaignId) => {
        return {
          url: `campaign/all-versions/${campaignId}`,
          method: "GET",
        };
      },
      providesTags: ["campaigns"],
    }),
    getVersionUsers: builder.query({
      query: (args) => {
        const { campaignId, versionId, page, rows } = args;
        return {
          url: `campaign/${campaignId}/version/${versionId}/users`,
          method: "GET",
          params: {
            page: page || 0,
            rows: rows || 25,
          },
        };
      },
    }),
    getSuppressionListUsers: builder.query({
      query: (args) => {
        const { versionId, page, rows } = args;
        return {
          url: `campaign/version/${versionId}/suppression-list/users`,
          method: "GET",
          params: {
            page: page || 0,
            rows: rows || 25,
          },
        };
      },
    }),
    getCampaignSuppressionLists: builder.query({
      query: (campaignId) => {
        return {
          url: `campaign/${campaignId}/suppression-lists`,
          method: "GET",
        };
      },
    }),
    getAllCampaignsWithVersions: builder.query({
      query: (args) => {
        const { pagination, sorting, searchQuery } = args || {};
        return {
          url: "campaign/with-versions",
          method: "GET",
          params: {
            page: pagination?.pageIndex || 0,
            rows: pagination?.pageSize || 25,
            sorting: sorting ? JSON.stringify(sorting) : undefined,
            search: searchQuery,
          },
        };
      },
      providesTags: ["campaigns"],
    }),
    saveCampaignDraft: builder.mutation({
      query: (body) => {
        const { campaignId } = body;
        return {
          url: `campaign/${campaignId}/save-draft`,
          method: "POST",
          body: {},
        };
      },
      invalidatesTags: (result, error, { campaignId }) => [
        { type: "campaigns", id: campaignId },
        "campaigns",
      ],
    }),
    uploadSuppressionCsv: builder.mutation({
      query: (body) => {
        const { campaignId, formData } = body;
        return {
          url: `campaign/${campaignId}/upload-suppression`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { campaignId }) => [
        { type: "campaigns", id: campaignId },
        "campaigns",
      ],
    }),
    cloneCampaign: builder.mutation({
      query: (campaignId) => ({
        url: `campaign/clone/${campaignId}`,
        method: "POST",
      }),
      invalidatesTags: ["campaigns"],
    }),
    updateCampaignStatus: builder.mutation({
      query: ({ campaignId, status }) => ({
        url: `campaign/status/${campaignId}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: "campaigns", id: campaignId },
        "campaigns",
      ],
    }),
  }),
});

export const {
  useCreateCampaignMutation,
  useGetCampaignsQuery,
  useGetCampaignUsersQuery,
  useGetSingleCampaignQuery,
  useGetGroupedCampaignsQuery,
  useGetApolloMessageQuery,
  useGetApolloMessageDetailQuery,
  useGetSendgridMessagesQuery,
  useGetDraftCampaignsQuery,
  useGetCampaignBatchNumbersQuery,
  useGetCampaignVersionHistoryQuery,
  useGetAllCampaignsWithVersionsQuery,
  useGetVersionUsersQuery,
  useGetSuppressionListUsersQuery,
  useGetCampaignSuppressionListsQuery,
  useSaveCampaignDraftMutation,
  useUploadSuppressionCsvMutation,
  useCloneCampaignMutation,
  useUpdateCampaignStatusMutation,
} = campaignApi;
